import { makeAutoObservable, runInAction } from "mobx";

import AppStore from "AppStore";
import ReportTimerStore from "pages/Projects/ReportModal/ReportTimerStore";
import messageBoxStore from "MessageBox/MessageBoxStore";

import client from "client";

const DEFAULT_LETTER_SEND_TASK_REWORK =
  "Добрый день.\n\nВы не прислали ответы на контрольные вопросы. Ответы являются обязательной частью задания!\n\nПри повторном нарушении сдачи отчета, будет сменен исполнитель без оплаты работы.";

class PopupStore {
  isOpen = false;
  modalType = null;

  constructor() {
    makeAutoObservable(this);
  }

  open(type) {
    this.isOpen = true;
    this.modalType = type;
  }

  close() {
    this.isOpen = false;
    this.agreed = false;
    this.amount = "";
    this.modalType = null;
  }

  // warranty popup
  agreed = false;
  setAgreed(value) {
    this.agreed = value;
  }

  warrantyAgreementReminderPopup = false;
  setWarrantyAgreementReminderPopup(value) {
    this.warrantyAgreementReminderPopup = value;
  }

  // sure modal
  text = "";
  ok = () => {};
  cancel = () => {};

  setText(text) {
    this.text = text;
  }

  setOk(func = () => {}) {
    this.ok = func;
  }

  setCancel(func = () => {}) {
    this.cancel = func;
  }

  // pay modal
  amount = "";
  setAmount(amount) {
    this.amount = amount;
  }

  get amountErrorText() {
    const amount = this.amount;
    if (!amount.length) return "";
    const number = Number(amount);
    if (!(number && number > 0 && number <= 1000000.0 && /^\d+$/.test(amount)))
      return "Введите сумму от 1 до 1 000 000";
    return "";
  }

  get isOkEnabled() {
    const amount = this.amount;
    if (!amount.length) return false;
    const number = Number(amount);
    return number && number > 0 && number <= 1000000.0 && /^\d+$/.test(amount);
  }

  okPay = async () => {
    if (!this.isOkEnabled) return;
    const link = await client.createDepositLink(Number(this.amount));
    if (link) window.location.replace(link);
  };

  isCheckboxChecked = false;
  setIsCheckboxChecked(value) {
    this.isCheckboxChecked = value;
  }

  // report modal
  reportData = undefined;
  projectData = undefined;
  showComment = false;
  showFAQModal = false;
  comment = "";
  statusText = "";
  searchPhrase = "";
  city = "";
  activityType = "";
  reportTimer = null;
  selectedQuestionIndex = null;
  selectedAnswers = [];
  sponsorId = null;

  openReportModal(reportData, projectData) {
    this.reportData = reportData;
    this.projectData = projectData;
    this.setComment(reportData.answer ? "" : DEFAULT_LETTER_SEND_TASK_REWORK);
    this.isOpen = true;
    this.startTimer();
  }

  closeReportModal() {
    this.isOpen = false;
    this.setShowComment(false);
    this.setShowFAQModal(false);
    this.stopTimer();
  }

  setShowFAQModal(showFAQModal) {
    this.showFAQModal = showFAQModal;
  }

  setSelectedQuestionIndex(index) {
    this.selectedQuestionIndex = index;
  }

  setSelectedAnswers(answers) {
    this.selectedAnswers = answers;
  }

  setShowComment(showComment) {
    this.showComment = showComment;
  }

  setComment(comment) {
    this.comment = comment;
  }

  setSearchPhrase(searchPhrase) {
    this.searchPhrase = searchPhrase;
  }

  setCity(city) {
    this.city = city;
  }

  setActivityType(activityType) {
    this.activityType = activityType;
  }

  setStatusText(statusText) {
    this.statusText = statusText;
  }

  setSponsorId(sponsorId) {
    this.sponsorId = sponsorId;
  }

  startTimer() {
    if (this.reportData?.status === "ready")
      this.reportTimer = new ReportTimerStore(this.reportData.acceptDatetime);
  }

  stopTimer() {
    if (this.reportTimer) {
      this.reportTimer.stopCountdown();
      this.reportTimer = null;
    }
  }

  getReportTimer() {
    if (this.reportData?.status === "ready") {
      return new ReportTimerStore(this.reportData.acceptDatetime);
    }
    return null;
  }

  getTimeUntilConfirmationReviews() {
    const report = this.reportData;
    if (!report || report.status !== "ready") {
      return null;
    }

    const isReview = this.activityType === "Отзыв";
    const hasWarranty = this.projectData?.activities?.find(
      (a) => a.type === "MAIN.REVIEW" && a.warranty
    );

    if (!isReview) {
      return null;
    }

    const readyDatetime = new Date(report.readyDatetime);
    const deadline = hasWarranty
      ? readyDatetime.getTime() + 120 * 60 * 60 * 1000 // 120ч - с гарантией
      : readyDatetime.getTime() + 48 * 60 * 60 * 1000; // 48ч - без гарантии

    const timer = new ReportTimerStore(report.readyDatetime);
    return deadline - Date.now() + timer.timeLeft;
  }

  isProcessingReport = false;
  async approve() {
    if (this.isProcessingReport) return;
    
    this.isProcessingReport = true;
    try {
      await client.approveReport(this.reportData.id);
      AppStore.setReportsAsApproved(this.projectData.id, this.reportData.id);
      this.isOpen = false;
      this.stopTimer();
    } catch (error) {
      messageBoxStore.showError("Ошибка при подтверждении отчета: " + error.message);
      throw error;
    } finally {
      runInAction(() => {
        this.isProcessingReport = false;
      });
    }
  }

  async rejectOrReplace(isReplace) {
    if (this.isProcessingReport) return;
    if (!this.comment) return messageBoxStore.showError("Добавьте комментарий");

    if (this.comment.trim().length < 4)
      return messageBoxStore.showError("Слишком короткий комментарий");

    this.isProcessingReport = true;
    try {
      this.stopTimer();

      const projectId = this.projectData.id;
      const reportId = this.reportData.id;

      if (isReplace) {
        await client.replaceReport(reportId, this.comment);
        AppStore.setReportAsReplaced(projectId, reportId);
      } else {
        await client.rejectReport(reportId, this.comment);
        AppStore.setReportAsRejected(projectId, reportId);
      }

      this.isOpen = false;
      this.setShowComment(false);
    } catch (error) {
      messageBoxStore.showError(
        "Ошибка при обработке отчета: " + error.message
      );
      throw error;
    } finally {
      runInAction(() => {
        this.isProcessingReport = false;
      });
    }
  }

  async reject() {
    return this.rejectOrReplace(false);
  }

  async replace() {
    return this.rejectOrReplace(true);
  }

  // project & project setup
  markValues = [50, 100, 200, 250, 350];
  baseAmount = 250;
  basePercentage = 30;

  valueReview = 1;
  setValueReview(newValue) {
    this.valueReview = newValue;
  }

  calculatePercentage(baseAmount, basePercentage, addition) {
    if (addition <= baseAmount) {
      return basePercentage;
    }
    const additionalAmount = addition - baseAmount;
    const additionalPercentage = (additionalAmount / 50) * 10;
    return Math.round(basePercentage + additionalPercentage);
  }

  getPercentageWithAddition(baseAmount, basePercentage, extraPrice, addition) {
    const newAmount = baseAmount + extraPrice + addition;
    return this.calculatePercentage(baseAmount, basePercentage, newAmount);
  }

  getPercentageWithoutAddition(baseAmount, basePercentage, extraPrice) {
    const newAmount = baseAmount + extraPrice;
    return this.calculatePercentage(baseAmount, basePercentage, newAmount);
  }

  selectedReport = null;
  setSelectedReport(report) {
    this.selectedReport = report;
  }

  showModalAuction = false;
  setShowModalAuction(value) {
    this.showModalAuction = value;
  }

  reportDataForRaisePrice = null;
  newExtraPriceForRaisePrice = null;
  agreedRulesRaisePrice = false;

  setAgreedRulesRaisePrice(value) {
    this.agreedRulesRaisePrice = value;
  }

  setReportDataForRaisePrice(reportData, newExtraPrice) {
    this.reportDataForRaisePrice = reportData;
    this.newExtraPriceForRaisePrice = newExtraPrice;
  }

  isUpdatingTask = false;
  async updateTaskExtraPrice(reportData, newExtraPrice) {
    if (this.isUpdatingTask) return;
  
    this.isUpdatingTask = true;
    try {
      const result = await client.boostTask(reportData.id, newExtraPrice);

      runInAction(() => {
        reportData.extraPrice = result.extraPrice;
        reportData.extraPriceUpdateDatetime = new Date().toISOString();

        if (result.extraPriceNext !== undefined) {
          reportData.extraPriceNext = result.extraPriceNext;
        } else {
          delete reportData.extraPriceNext;
        }
      });

      return result;
    } finally {
      runInAction(() => {
        this.isUpdatingTask = false;
      });
    }
  }

  cancelTaskWithConfirm(reportData) {
    if (reportData.status !== "run" || reportData.workerId || reportData.cancelling) {
      return;
    }
    popupStore.setText("Вы действительно хотите отменить выполнение задачи?");
    popupStore.setOk(() => { this.cancelTask(reportData) });
    popupStore.open("suremodal");
  }

  isCancellingTask = false;
  async cancelTask(reportData) {
    if (this.isCancellingTask) return;

    if (reportData.status !== "run" || reportData.workerId || reportData.cancelling) {
      return;
    }

    this.isCancellingTask = true;
    try {
      await client.cancelTask(reportData.id);
      AppStore.updateProjects();
      messageBoxStore.showWarning([
        "Вы остановили задачу.",
        <br />,
        <br />,
        "Сервис вернет вам замороженные деньги по данной задаче на ваш баланс.",
      ]);
    } finally {
      runInAction(() => {
        this.isCancellingTask = false;
      });
    }
  }

  markValuesProject = [3, 5, 7, 10, 12, 15, 17, 20, 22, 25];
  valueProject = 2;

  setValueProject(newValue) {
    this.valueProject = newValue;
  }

  roundToCents(value) {
    return Math.round(value * 100) / 100;
  }

  newExtraPriceForRaiseProjectPrice = null;
  projectDataForRaisePrice = null;
  setProjectDataForRaisePrice(projectData, extraPriceNew) {
    this.projectDataForRaisePrice = projectData;
    this.newExtraPriceForRaiseProjectPrice = extraPriceNew;
  }

  getApplicableTasks(projectData) {
    return projectData.reports?.filter(report => {
      const isReview = report.chain?.includes("MAIN.REVIEW");
      const isValidStatus = ["new", "run"].includes(report.status);
      const isNotProcessing = !report.cancelling;
      
      return !isReview && isValidStatus && isNotProcessing;
    }) || [];
  }

  isUpdatingProject = false;
  async updateProjectExtraPrice(projectData, extraPriceNew) {
    if (this.isUpdatingProject) return;

    this.isUpdatingProject = true;
    try {
      const balanceBefore = await client.getBalance();
      const applicableTasks = this.getApplicableTasks(projectData);
      const taskIds = applicableTasks.map(task => task.id);
      
      const result = await client.boostProject(projectData.id, taskIds, extraPriceNew);
      
      const balanceAfter = await client.getBalance();
      const actualDeducted = this.roundToCents(balanceBefore - balanceAfter);

      runInAction(() => {
        applicableTasks.forEach(task => {
          if (task.status === "new") {
            task.extraPrice = extraPriceNew;
            task.extraPriceNext = undefined;
          } else if (task.status === "run") {
            task.extraPriceNext = extraPriceNew;
          }
        });

        projectData.extraPrice = extraPriceNew;
        projectData.extraPriceNext = applicableTasks.some(t => t.status === "run") ? extraPriceNew : undefined;
        projectData.extraPriceUpdateDatetime = new Date().toISOString();
      });

      const newTasksCount = applicableTasks.filter(t => t.status === "new").length;
      const runningTasksCount = applicableTasks.filter(t => t.status === "run").length;

      let message = [`Установлена наценка: ${extraPriceNew} ₽ на задачу`];
      if (actualDeducted > 0) { message.push(<br />, `Зарезервировано: ${actualDeducted} ₽`); }
      if (newTasksCount > 0) { message.push(<br />, `${newTasksCount} новым задачам применено сразу`); }
      if (runningTasksCount > 0) { message.push(<br />, `${runningTasksCount} задачам в работе применится после завершения`); }
      
      messageBoxStore.showInfo(message);
      return { ...result, actualDeducted };
      
    } catch (error) {
      messageBoxStore.showError("Ошибка при установке наценки: " + error.message);
      throw error;
    } finally {
      runInAction(() => { this.isUpdatingProject = false; });
    }
  }

  async cancelProjectBoost(projectData) {
    if (this.isUpdatingProject) return;

    this.isUpdatingProject = true;
    try {
      const balanceBefore = await client.getBalance();
      const applicableTasks = this.getApplicableTasks(projectData);
      const taskIds = applicableTasks.map(task => task.id);
      
      const result = await client.boostProject(projectData.id, taskIds, 0);
      
      const balanceAfter = await client.getBalance();
      const actualReturned = this.roundToCents(balanceAfter - balanceBefore);

      runInAction(() => {
        if (result.success && result.result?.length > 0) {
          result.result.forEach((taskResult, index) => {
            const taskId = taskIds[index];
            const reportToUpdate = projectData.reports?.find(report => report.id === taskId);
            
            if (reportToUpdate) {
              reportToUpdate.extraPrice = taskResult.extraPrice;
              if (taskResult.extraPriceNext !== undefined) {
                reportToUpdate.extraPriceNext = taskResult.extraPriceNext;
              } else {
                delete reportToUpdate.extraPriceNext;
              }
            }
          });

          const remainingPrices = result.result.filter(r => r.extraPrice > 0).map(r => r.extraPrice);
              
          projectData.extraPrice = remainingPrices.length > 0 ? this.roundToCents(Math.max(...remainingPrices)) : 0;
          projectData.extraPriceUpdateDatetime = new Date().toISOString();

          const pendingCancellation = result.result.some(r => r.extraPriceNext !== undefined);
          if (pendingCancellation) {
            const pendingValues = result.result
              .filter(r => r.extraPriceNext !== undefined)
              .map(r => r.extraPriceNext);
            
            if (pendingValues.length > 0) {
              projectData.extraPriceNext = Math.max(...pendingValues);
            }
          } else {
            delete projectData.extraPriceNext;
            if (!pendingCancellation) {
              projectData.extraPrice = 0;
            }
          }
        }
      });

      if (!result.success) {
        if (result.insufficient) {
          messageBoxStore.showError("Ошибка при отмене наценки проекта.");
        } else if (result.reload) {
          messageBoxStore.showError("Данные проекта устарели. Обновите страницу и попробуйте снова.");
        } else {
          messageBoxStore.showError(result.message || "Ошибка при отмене наценки проекта.");
        }
        return result;
      }

      if (actualReturned > 0) {
        messageBoxStore.showInfo([
          "Наценка проекта отменяется.",
          <br />,
          <br />,
          "Средства будут возвращены по мере отмены наценки с задач."
        ]);
      } else {
        messageBoxStore.showInfo("Наценка проекта отменяется. Средства будут возвращены по мере отмены наценки с задач.");
      }
      
      return { ...result, actualReturned };
      
    } catch (error) {
      messageBoxStore.showError("Ошибка при отмене наценки проекта: " + error.message);
      throw error;
    } finally {
      runInAction(() => {
        this.isUpdatingProject = false;
      });
    }
  }

  calculateImplementedBoost(project) {
    if (!project.reports) return 0;
    
    return this.roundToCents(
      project.reports
        .filter(report => 
          report.status === "finished" && 
          !report.chain?.includes("MAIN.REVIEW")
        )
        .reduce((total, report) => total + (report.extraPrice || 0), 0)
    );
  }

  calculateCurrentBoost(project) {
    if (!project.reports) return 0;
    
    return this.roundToCents(
      project.reports
        .filter(report => 
          ["new", "run"].includes(report.status) && 
          !report.chain?.includes("MAIN.REVIEW")
        )
        .reduce((total, report) => total + (report.extraPrice || 0), 0)
    );
  }

  showModalAuctionProject = false;
  setShowModalAuctionProject(value) {
    this.showModalAuctionProject = value;
  }
  
  selectedProject = null;
  setSelectedProject(project) {
    this.selectedProject = project;
  }
}

const popupStore = new PopupStore();
export default popupStore;
